// CharacterBuilder — full guided character creation with a live sheet preview.
// Supports Standard Array, Point Buy (27), and rolled (4d6 drop lowest) scores;
// race/subrace/class/subclass/background/alignment; skill & spell selection.
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import {
  ABILITY_KEYS,
  type AbilityKey,
  type BuildCharacterRequest,
  type ClassSpellsResponse,
  type ControllerKind,
  type FullCharacter,
  type Scores,
  type SrdMeta,
} from "../lib/types";
import { ABILITY_ABBR, abilityMod, signed, titleizeSlug } from "../lib/format";
import { SKILL_LABELS, STANDARD_ARRAY } from "../lib/srd";
import {
  ALL_SKILLS, CLASS_SKILLS, cantripsKnown, spellsKnown,
  recommendedArray, recommendedSkills, firstN,
} from "./srdBudgets";
import { CharacterSheet } from "./CharacterSheet";
import { OptionInfo } from "./OptionInfo";
import { ClassProgressionPanel } from "./ClassProgressionPanel";
import { Spinner, Modal } from "./ui";
import { useToast } from "../hooks/useToast";

type ScoreMethod = "array" | "pointbuy" | "roll";
const FLOOR: Scores = { str: 8, dex: 8, con: 8, int: 8, wis: 8, cha: 8 };

export function CharacterBuilder({
  meta,
  initialName,
  controllerKind,
  onCancel,
  onCommit,
}: {
  meta: SrdMeta;
  initialName?: string;
  controllerKind: ControllerKind;
  onCancel: () => void;
  onCommit: (req: BuildCharacterRequest, persona: string, label: string) => Promise<void>;
}) {
  const toast = useToast();
  const [name, setName] = useState(initialName || "");
  const [raceIndex, setRaceIndex] = useState(meta.races[0]?.index || "human");
  const [classIndex, setClassIndex] = useState(meta.classes[0]?.index || "fighter");
  const [bgIndex, setBgIndex] = useState(meta.backgrounds[0]?.index || "");
  const [alignment, setAlignment] = useState(meta.alignments[0]?.index || "true-neutral");
  const [level, setLevel] = useState(1);
  const [subraceIndex, setSubraceIndex] = useState<string | null>(null);
  const [subclassIndex, setSubclassIndex] = useState<string | null>(null);

  const [method, setMethod] = useState<ScoreMethod>("array");
  const [scores, setScores] = useState<Scores>({ str: 15, dex: 14, con: 13, int: 12, wis: 10, cha: 8 });
  const [arrayAssign, setArrayAssign] = useState<Record<AbilityKey, number | null>>({
    str: 15, dex: 14, con: 13, int: 12, wis: 10, cha: 8,
  });

  const [skills, setSkills] = useState<string[]>([]);
  const [expertise, setExpertise] = useState<string[]>([]);
  const [chosenFeats, setChosenFeats] = useState<string[]>([]);
  const [showProgression, setShowProgression] = useState(false);
  const [featOptions, setFeatOptions] = useState<{ index: string; name: string; desc: string; category: string; grants_skills: number }[]>([]);
  const [cantrips, setCantrips] = useState<string[]>([]);
  const [spells, setSpells] = useState<string[]>([]);
  const [persona, setPersona] = useState("");

  const [preview, setPreview] = useState<FullCharacter | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [classSpells, setClassSpells] = useState<ClassSpellsResponse | null>(null);

  const cls = meta.classes.find((c) => c.index === classIndex);
  const subraces = meta.subraces.filter((s) => s.race === raceIndex);
  const isCaster = !!cls?.spellcasting;

  // Reset subrace/subclass when their parent changes.
  useEffect(() => { setSubraceIndex(subraces[0]?.index ?? null); }, [raceIndex]); // eslint-disable-line
  useEffect(() => {
    if (level >= 3 && cls?.subclasses.length) setSubclassIndex(cls.subclasses[0].index);
    else setSubclassIndex(null);
  }, [classIndex, level]); // eslint-disable-line

  // Load class spell list for casters.
  useEffect(() => {
    if (!isCaster) { setClassSpells(null); return; }
    let alive = true;
    api.classSpells(classIndex).then((r) => alive && setClassSpells(r)).catch(() => {});
    setCantrips([]); setSpells([]);
    return () => { alive = false; };
  }, [classIndex, isCaster]);

  // Load the feat catalog once.
  useEffect(() => {
    let alive = true;
    api.feats().then((r) => alive && setFeatOptions(r.feats)).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Compute the effective scores from the chosen method.
  const effectiveScores: Scores = useMemo(() => {
    if (method === "array") {
      const s: Scores = { ...FLOOR };
      for (const ab of ABILITY_KEYS) s[ab] = arrayAssign[ab] ?? 8;
      return s;
    }
    return scores;
  }, [method, scores, arrayAssign]);

  // Debounced live preview.
  const debounceRef = useRef<number | null>(null);
  useEffect(() => {
    if (!name.trim()) { setPreview(null); return; }
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(async () => {
      setPreviewing(true);
      try {
        const req = buildRequest();
        const p = await api.previewCharacter(req);
        setPreview(p);
      } catch (e) {
        /* keep last good preview */
      } finally {
        setPreviewing(false);
      }
    }, 350);
    return () => { if (debounceRef.current) window.clearTimeout(debounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, raceIndex, subraceIndex, classIndex, subclassIndex, bgIndex, alignment, level,
      JSON.stringify(effectiveScores), skills.join(), cantrips.join(), spells.join(), expertise.join(), chosenFeats.join()]);

  function buildRequest(): BuildCharacterRequest {
    return {
      name: name.trim() || "Adventurer",
      race_index: raceIndex,
      class_index: classIndex,
      background_index: bgIndex || null,
      subrace_index: subraceIndex,
      subclass_index: level >= 3 ? subclassIndex : null,
      level,
      scores: effectiveScores,
      skill_proficiencies: skills,
      skill_expertise: expertise.filter((s) => skills.includes(s)),
      feats: chosenFeats,
      alignment,
      cantrips,
      spells,
      controller_kind: controllerKind,
    };
  }

  // ── point buy ──
  const pbCost = useMemo(() => {
    if (method !== "pointbuy") return 0;
    return ABILITY_KEYS.reduce((sum, ab) => sum + (meta.point_buy_costs[String(scores[ab])] ?? 0), 0);
  }, [method, scores, meta.point_buy_costs]);
  const pbValid = pbCost <= 27 && ABILITY_KEYS.every((ab) => scores[ab] >= 8 && scores[ab] <= 15);

  // ── array assignment ──
  const arrayUsed = Object.values(arrayAssign).filter((v) => v !== null) as number[];
  function assignArrayValue(ab: AbilityKey, value: number | null) {
    setArrayAssign((prev) => {
      const next = { ...prev };
      // If the value is taken by another ability, swap.
      if (value !== null) {
        for (const k of ABILITY_KEYS) if (k !== ab && next[k] === value) next[k] = prev[ab];
      }
      next[ab] = value;
      return next;
    });
  }

  async function rollArray() {
    try {
      const r = await api.abilityArray();
      const vals = r.totals;
      const next: Record<AbilityKey, number | null> = {} as any;
      ABILITY_KEYS.forEach((ab, i) => (next[ab] = vals[i] ?? 8));
      setArrayAssign(next);
      toast.push(`Rolled: ${vals.join(", ")}`, "success");
    } catch {
      toast.push("Couldn't roll scores", "error");
    }
  }

  // Class-gated skills: pick from the class's list, up to its skill count.
  const classSkills = CLASS_SKILLS[classIndex];
  const skillChoices = classSkills?.list ?? ALL_SKILLS;
  const skillCap = classSkills?.count;

  // Prune skill picks to the current class's list + count when the class changes.
  useEffect(() => {
    setSkills((prev) => {
      const pruned = prev.filter((s) => skillChoices.includes(s)).slice(0, skillCap ?? prev.length);
      return pruned.length === prev.length && pruned.every((v, i) => v === prev[i]) ? prev : pruned;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classIndex]);

  // Spell/cantrip budgets for the chosen class + level (null → uncapped).
  const cantripCap = cantripsKnown(classIndex, level);
  const spellCap = spellsKnown(classIndex, level);

  // Keep spell picks within budget when class/level changes.
  useEffect(() => {
    if (cantripCap != null) setCantrips((p) => (p.length <= cantripCap ? p : p.slice(0, cantripCap)));
    if (spellCap != null) setSpells((p) => (p.length <= spellCap ? p : p.slice(0, spellCap)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classIndex, level]);

  // Expertise: Rogue gains 2 at L1 and 2 more at L6; Bard gains 2 at L3 and 2 more at L10.
  // (SRD RAW; Rogue may also expertise Thieves' Tools but we model skills only.)
  const expertiseSlots = useMemo(() => {
    if (classIndex === "rogue") return level >= 6 ? 4 : 2;
    if (classIndex === "bard") return level >= 10 ? 4 : level >= 3 ? 2 : 0;
    return 0;
  }, [classIndex, level]);

  // Drop expertise picks that are no longer proficient or exceed the slot budget.
  useEffect(() => {
    setExpertise((prev) => {
      const pruned = prev.filter((s) => skills.includes(s)).slice(0, expertiseSlots);
      return pruned.length === prev.length && pruned.every((v, i) => v === prev[i]) ? prev : pruned;
    });
  }, [skills, expertiseSlots]);

  // Feats: gained at ASI levels (4/8/12/16/19), plus Fighter gets a bonus at 6, Rogue at 10.
  const featSlots = useMemo(() => {
    const asiLevels = [4, 8, 12, 16, 19];
    let n = asiLevels.filter((l) => level >= l).length;
    if (classIndex === "fighter" && level >= 6) n += 1;
    if (classIndex === "rogue" && level >= 10) n += 1;
    return n;
  }, [classIndex, level]);

  // Keep chosen feats within budget when level/class changes.
  useEffect(() => {
    setChosenFeats((prev) => (prev.length <= featSlots ? prev : prev.slice(0, featSlots)));
  }, [featSlots]);


  function toggle(list: string[], set: (v: string[]) => void, key: string, max?: number) {
    if (list.includes(key)) set(list.filter((k) => k !== key));
    else if (!max || list.length < max) set([...list, key]);
  }

  // Quick build — fill a ready-to-play recommended character so a newcomer can skip
  // the 30 micro-decisions: standard array assigned by class priority, the class's
  // default skills, and (for casters) a starter spell loadout. Everything stays
  // editable afterward; the live preview updates immediately.
  function quickBuild() {
    setMethod("array");
    const arr = recommendedArray(classIndex);
    setArrayAssign({ ...arr });
    setSkills(recommendedSkills(classIndex));
    setExpertise([]);
    setChosenFeats([]);
    if (isCaster && classSpells) {
      const cantripList = classSpells.by_level["0"] ?? [];
      setCantrips(firstN(cantripList, cantripCap));
      // Take known spells from the lowest levels up (level 1 first) as a sensible start.
      const leveled = Object.entries(classSpells.by_level)
        .filter(([lvl]) => lvl !== "0")
        .sort(([a], [b]) => Number(a) - Number(b))
        .flatMap(([, list]) => list);
      setSpells(firstN(leveled, spellCap));
    } else {
      setCantrips([]); setSpells([]);
    }
    if (!name.trim()) setName(suggestName(classIndex));
    toast.push("Filled a recommended build — tweak anything you like", "success");
  }

  async function commit() {
    if (!name.trim()) { toast.push("Give your character a name", "error"); return; }
    if (method === "pointbuy" && !pbValid) {
      toast.push(`Point buy is over budget (${pbCost}/27). Lower a score before seating.`, "error");
      return;
    }
    setCommitting(true);
    try {
      await onCommit(buildRequest(), persona, name.trim());
    } catch (e) {
      toast.push(e instanceof Error ? e.message : "Failed to create character", "error");
    } finally {
      setCommitting(false);
    }
  }

  return (
    <div className="builder">
      <div className="builder__form">
        <div className="row-between" style={{ alignItems: "center", gap: 10 }}>
          <h2 style={{ margin: 0 }}>{controllerKind === "ai" ? "Summon an AI companion" : "Forge a character"}</h2>
          <button type="button" className="btn btn--ghost btn--sm" onClick={quickBuild}
            title="Fill a recommended, ready-to-play build for the chosen class — edit anything after">
            ⚡ Quick build
          </button>
        </div>
        <div className="hint" style={{ marginTop: 4, marginBottom: 8 }}>
          New here? Hit <b>Quick build</b> for a solid ready-to-play {titleizeSlug(classIndex)}, then adjust to taste.
        </div>

        <div className="builder__section">
          <label className="label">Name</label>
          <input className="field" value={name} autoFocus
            onChange={(e) => setName(e.target.value)} placeholder="e.g. Bram Ironwood" />
        </div>

        <div className="form-grid2">
          <div className="builder__section">
            <label className="label">Race <OptionInfo kind="races" index={raceIndex} label="race" /></label>
            <select className="field" value={raceIndex} onChange={(e) => setRaceIndex(e.target.value)}>
              {meta.races.map((r) => <option key={r.index} value={r.index}>{r.name}</option>)}
            </select>
            {subraces.length > 0 && (
              <div className="row-inline mt8">
                <select className="field" value={subraceIndex ?? ""} onChange={(e) => setSubraceIndex(e.target.value || null)}>
                  {subraces.map((s) => <option key={s.index} value={s.index}>{s.name}</option>)}
                </select>
                {subraceIndex && <OptionInfo kind="subraces" index={subraceIndex} label="lineage" />}
              </div>
            )}
          </div>
          <div className="builder__section">
            <label className="label">
              Class <OptionInfo kind="classes" index={classIndex} label="class" />
              <button type="button" className="opt-info-btn opt-info-btn--text"
                onClick={() => setShowProgression(true)}>level chart</button>
            </label>
            <select className="field" value={classIndex} onChange={(e) => setClassIndex(e.target.value)}>
              {meta.classes.map((c) => (
                <option key={c.index} value={c.index}>{c.name}{c.spellcasting ? " ✦" : ""}</option>
              ))}
            </select>
            {level >= 3 && cls?.subclasses.length ? (
              <div className="row-inline mt8">
                <select className="field" value={subclassIndex ?? ""} onChange={(e) => setSubclassIndex(e.target.value || null)}>
                  {cls.subclasses.map((s) => <option key={s.index} value={s.index}>{s.name}</option>)}
                </select>
                {subclassIndex && <OptionInfo kind="subclasses" index={subclassIndex} label="subclass" />}
              </div>
            ) : null}
          </div>
        </div>

        <div className="form-grid2">
          <div className="builder__section">
            <label className="label">Background <OptionInfo kind="backgrounds" index={bgIndex} label="background" /></label>
            <select className="field" value={bgIndex} onChange={(e) => setBgIndex(e.target.value)}>
              {meta.backgrounds.map((b) => <option key={b.index} value={b.index}>{b.name}</option>)}
            </select>
          </div>
          <div className="builder__section">
            <label className="label">Alignment <OptionInfo kind="alignments" index={alignment} label="alignment" /></label>
            <select className="field" value={alignment} onChange={(e) => setAlignment(e.target.value)}>
              {meta.alignments.map((a) => <option key={a.index} value={a.index}>{a.name}</option>)}
            </select>
          </div>
        </div>

        <div className="builder__section">
          <label className="label">Level — {level}</label>
          <input type="range" min={1} max={20} value={level} style={{ width: "100%" }}
            onChange={(e) => setLevel(Number(e.target.value))} />
        </div>

        {/* ── ability scores ── */}
        <div className="builder__section">
          <div className="row-between" style={{ marginBottom: 10 }}>
            <label className="label" style={{ margin: 0 }}>Ability Scores</label>
            <div className="seg">
              <button className={method === "array" ? "on" : ""} onClick={() => setMethod("array")}>Array</button>
              <button className={method === "pointbuy" ? "on" : ""} onClick={() => setMethod("pointbuy")}>Point Buy</button>
              <button className={method === "roll" ? "on" : ""} onClick={() => setMethod("roll")}>Manual</button>
            </div>
          </div>

          {method === "array" && (
            <>
              <div className="score-grid">
                {ABILITY_KEYS.map((ab) => (
                  <div className="score-box" key={ab}>
                    <div className="ab">{ABILITY_ABBR[ab]} <OptionInfo kind="ability-scores" index={ab} label={ABILITY_ABBR[ab]} /></div>
                    <select className="field" value={arrayAssign[ab] ?? ""}
                      onChange={(e) => assignArrayValue(ab, e.target.value ? Number(e.target.value) : null)}>
                      <option value="">—</option>
                      {STANDARD_ARRAY.map((v) => (
                        <option key={v} value={v}
                          disabled={arrayUsed.includes(v) && arrayAssign[ab] !== v}>{v}</option>
                      ))}
                    </select>
                    <div className="mod">{signed(abilityMod((arrayAssign[ab] ?? 8) + racialBonus(ab, preview)))}</div>
                  </div>
                ))}
              </div>
              <div className="hint">Standard array: {STANDARD_ARRAY.join(", ")}. Racial bonuses shown in modifier.</div>
            </>
          )}

          {method === "pointbuy" && (
            <>
              <div className="score-grid">
                {ABILITY_KEYS.map((ab) => (
                  <div className="score-box" key={ab}>
                    <div className="ab">{ABILITY_ABBR[ab]} <OptionInfo kind="ability-scores" index={ab} label={ABILITY_ABBR[ab]} /></div>
                    <div className="val">{scores[ab]}</div>
                    <div className="mod">{signed(abilityMod(scores[ab] + racialBonus(ab, preview)))}</div>
                    <div className="steppers">
                      <button disabled={scores[ab] <= 8}
                        onClick={() => setScores((s) => ({ ...s, [ab]: s[ab] - 1 }))}>−</button>
                      <button disabled={scores[ab] >= 15}
                        onClick={() => setScores((s) => ({ ...s, [ab]: s[ab] + 1 }))}>+</button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="pointbuy-bar">
                <span>Point Buy budget</span>
                <span className={`cost ${pbValid ? "ok" : "over"}`}>{pbCost} / 27</span>
              </div>
            </>
          )}

          {method === "roll" && (
            <>
              <div className="score-grid">
                {ABILITY_KEYS.map((ab) => (
                  <div className="score-box" key={ab}>
                    <div className="ab">{ABILITY_ABBR[ab]} <OptionInfo kind="ability-scores" index={ab} label={ABILITY_ABBR[ab]} /></div>
                    <div className="val">{scores[ab]}</div>
                    <div className="mod">{signed(abilityMod(scores[ab] + racialBonus(ab, preview)))}</div>
                    <div className="steppers">
                      <button disabled={scores[ab] <= 1} onClick={() => setScores((s) => ({ ...s, [ab]: s[ab] - 1 }))}>−</button>
                      <button disabled={scores[ab] >= 20} onClick={() => setScores((s) => ({ ...s, [ab]: s[ab] + 1 }))}>+</button>
                    </div>
                  </div>
                ))}
              </div>
              <button className="btn btn--sm mt12" onClick={rollArray}>🎲 Roll 4d6 drop lowest</button>
              <span className="hint" style={{ marginLeft: 10 }}>Assigns rolled values in order; tweak with steppers.</span>
            </>
          )}
        </div>

        {/* ── skills ── */}
        <div className="builder__section">
          <label className="label">
            Skill Proficiencies <span className={`pick-count${skillCap && skills.length >= skillCap ? " over" : ""}`}>({skills.length}{skillCap ? `/${skillCap}` : ""})</span>
            <span className="hint-inline"> — from your class list; background skills are added automatically</span>
          </label>
          <div className="pick-grid">
            {skillChoices.map((sk) => {
              const on = skills.includes(sk);
              const atCap = !!skillCap && skills.length >= skillCap && !on;
              return (
                <button key={sk} className={`pick${on ? " pick--on" : ""}${atCap ? " pick--disabled" : ""}`}
                  disabled={atCap}
                  onClick={() => toggle(skills, setSkills, sk, skillCap)}>{SKILL_LABELS[sk]}</button>
              );
            })}
          </div>
        </div>

        {/* ── expertise (Rogue/Bard) ── */}
        {expertiseSlots > 0 && (
          <div className="builder__section">
            <label className="label">
              Expertise <span className="pick-count">({expertise.length}/{expertiseSlots})</span>
              <span className="hint-inline"> — double proficiency; pick from your proficient skills</span>
            </label>
            {skills.length === 0 ? (
              <div className="muted-note">Choose skill proficiencies first.</div>
            ) : (
              <div className="pick-grid">
                {skills.map((sk) => (
                  <button key={sk} className={`pick pick--gold${expertise.includes(sk) ? " pick--on" : ""}`}
                    onClick={() => toggle(expertise, setExpertise, sk, expertiseSlots)}>
                    {SKILL_LABELS[sk]}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── feats ── */}
        {featSlots > 0 && (
          <div className="builder__section">
            <label className="label">
              Feats <span className="pick-count">({chosenFeats.length}/{featSlots})</span>
              <span className="hint-inline"> — taken at ASI levels; each replaces an ability score increase</span>
            </label>
            <div className="feat-list">
              {featOptions.map((f) => {
                const on = chosenFeats.includes(f.index);
                const atCap = chosenFeats.length >= featSlots && !on;
                return (
                  <button
                    key={f.index}
                    className={`feat-card${on ? " feat-card--on" : ""}${atCap ? " feat-card--disabled" : ""}`}
                    onClick={() => !atCap && toggle(chosenFeats, setChosenFeats, f.index, featSlots)}
                  >
                    <div className="feat-card__head">
                      <span className="feat-card__name">{f.name}</span>
                      <span className="feat-card__cat">{f.category}</span>
                    </div>
                    <div className="feat-card__desc">{f.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── spells ── */}
        {isCaster && classSpells && (
          <div className="builder__section">
            <label className="label">Spells <span className="hint-inline">(pick your cantrips & known spells)</span></label>
            <div className="spell-levels">
              {Object.entries(classSpells.by_level).map(([lvl, list]) => {
                const isCantrip = lvl === "0";
                const selected = isCantrip ? cantrips : spells;
                const cap = isCantrip ? cantripCap : spellCap;
                const chosen = list.filter((s) => selected.includes(s.index)).length;
                // The cap is per-kind across ALL levels — grey out unpicked spells
                // once the whole known/cantrip budget is spent.
                const atBudget = cap != null && selected.length >= cap;
                return (
                  <div className="spell-level-group" key={lvl}>
                    <div className="slg-head">
                      {isCantrip ? "Cantrips" : `Level ${lvl}`}
                      {cap != null
                        ? <span className={`pick-count${selected.length >= cap ? " over" : ""}`}> ({selected.length}/{cap})</span>
                        : chosen > 0 && <span className="pick-count"> ({chosen} chosen)</span>}
                    </div>
                    <div className="pick-grid">
                      {list.map((s) => {
                        const on = selected.includes(s.index);
                        const atCap = atBudget && !on;
                        return (
                          <span key={s.index} className="spell-pick">
                            <button
                              className={`pick${on ? " pick--on" : ""}${atCap ? " pick--disabled" : ""}`}
                              disabled={atCap}
                              onClick={() => {
                                if (isCantrip) toggle(cantrips, setCantrips, s.index, cantripCap ?? undefined);
                                else toggle(spells, setSpells, s.index, spellCap ?? undefined);
                              }}>{s.name}</button>
                            <OptionInfo kind="spells" index={s.index} label={s.name} />
                          </span>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="hint" style={{ marginTop: 8 }}>
              Pick the cantrips and spells you want at this level. You can change them any time later
              from the character sheet's ✎ Edit menu.
            </div>
          </div>
        )}

        {/* ── AI persona ── */}
        {controllerKind === "ai" && (
          <div className="builder__section">
            <label className="label">AI Persona & Play-style</label>
            <textarea className="field" value={persona} rows={3}
              onChange={(e) => setPersona(e.target.value)}
              placeholder="How does this character think and act? e.g. 'A cautious tactician who protects allies and speaks rarely but with weight.'" />
            <div className="hint">This shapes how the AI roleplays and fights as {name || "this character"}.</div>
          </div>
        )}

        <div className="modal__row">
          <button className="btn btn--ghost" onClick={onCancel}>Cancel</button>
          <button className="btn btn--primary"
            disabled={committing || !name.trim() || (method === "pointbuy" && !pbValid)}
            title={method === "pointbuy" && !pbValid ? `Point buy is over budget (${pbCost}/27)` : undefined}
            onClick={commit}>
            {committing ? <Spinner /> : controllerKind === "ai" ? "Seat Companion" : "Seat Character"}
          </button>
        </div>
      </div>

      <div className="builder__preview">
        {previewing && !preview && <div className="muted flex"><Spinner /> Building…</div>}
        {preview ? <CharacterSheet ch={preview} /> : (
          !previewing && <div className="muted" style={{ textAlign: "center", paddingTop: 40 }}>
            Name your character to see the live sheet.
          </div>
        )}
      </div>

      {showProgression && (
        <Modal onClose={() => setShowProgression(false)}>
          <h2>{titleizeSlug(classIndex)} — Level Progression</h2>
          <div className="modal__sub">What you gain at every level: proficiency bonus, features, ability score improvements, and spell slots.</div>
          <ClassProgressionPanel classIndex={classIndex} currentLevel={level} />
        </Modal>
      )}
    </div>
  );
}

// A quick evocative name suggestion per class, used only when Quick build runs
// with an empty name field so the character is instantly seat-ready.
const QUICK_NAMES: Record<string, string[]> = {
  barbarian: ["Brakka", "Ulf Stonefist", "Varra"],
  bard: ["Lyric Meadowlark", "Cassian Vell", "Neridia"],
  cleric: ["Sister Brona", "Aldric Dawnward", "Miriael"],
  druid: ["Thornwick", "Elowen", "Fael Greenmoor"],
  fighter: ["Bram Ironwood", "Sera Blackshield", "Corin"],
  monk: ["Kaito", "Sister Wu", "Ren Ashfoot"],
  paladin: ["Ser Aldous", "Dame Elira", "Gareth Truelight"],
  ranger: ["Fenn Wildstride", "Sylvie Thorn", "Bram Longshot"],
  rogue: ["Pip Nightingale", "Vex", "Sable Quickfingers"],
  sorcerer: ["Ember Vale", "Nyx", "Caldwin"],
  warlock: ["Morrigan Dusk", "Thane Hollow", "Livia"],
  wizard: ["Alden Quill", "Isolde", "Merrick Vane"],
};
function suggestName(classIndex: string): string {
  const pool = QUICK_NAMES[classIndex] ?? ["Adventurer"];
  return pool[Math.floor(Math.random() * pool.length)];
}

function racialBonus(ab: AbilityKey, preview: FullCharacter | null): number {
  // The preview API returns the per-ability breakdown (base · racial · item);
  // read the racial layer straight from it so the grid's modifier matches the
  // live sheet. No preview yet (e.g. before a name is entered) → 0.
  return preview?.score_breakdown?.[ab]?.racial ?? 0;
}
